import type { RoleDefinition, SupportedLocale } from '../../types/role-hub';
import { ROLE_HUB_CONFIG } from './role-hub-config';

const lookupByLocale: Partial<Record<SupportedLocale, Map<string, RoleDefinition>>> = {};

function lookupFor(locale: SupportedLocale): Map<string, RoleDefinition> {
  let map = lookupByLocale[locale];
  if (!map) {
    map = new Map();
    for (const role of ROLE_HUB_CONFIG) {
      for (const value of role.titleValues[locale] ?? []) {
        map.set(value.trim().toLowerCase(), role);
      }
    }
    lookupByLocale[locale] = map;
  }
  return map;
}

/**
 * The configured role hub a job's `normalisedTitle` (in the page locale) belongs to, or null.
 * Exact match against the role's title values, case-insensitive: the same rule the roles
 * sitemap counts by, so a job links to a hub only when it would be listed on it.
 */
export function findRoleForNormalisedTitle(
  normalisedTitle: string | null | undefined,
  locale: SupportedLocale,
): RoleDefinition | null {
  const key = (normalisedTitle ?? '').trim().toLowerCase();
  if (!key) return null;
  return lookupFor(locale).get(key) ?? null;
}

/**
 * Same lookup across all three locales: for titles whose language is not known (a company
 * profile's topRoles, a salary page's role title), the first locale that matches wins.
 */
export function findRoleForAnyLocaleTitle(title: string | null | undefined): RoleDefinition | null {
  for (const locale of ['fi', 'en', 'sv'] as const) {
    const role = findRoleForNormalisedTitle(title, locale);
    if (role) return role;
  }
  return null;
}

/** Role hub whose display name (any locale) equals the given title, case-insensitive. */
export function findRoleByDisplayName(title: string | null | undefined): RoleDefinition | null {
  const key = (title ?? '').trim().toLowerCase();
  if (!key) return null;
  return ROLE_HUB_CONFIG.find((r) => Object.values(r.displayNames).some((n) => n.trim().toLowerCase() === key)) ?? null;
}
